import { mock, MockProxy } from 'jest-mock-extended';
import { Logger, PackageSchema, Step, StepEvent, StepPackage, WorkflowContext } from '@samelogic/steps';
import { StepConfig } from '../../../models';
import { StepContainer } from './StepContainer';
import { schema } from '../../__tests__/MockPackage';
import { contextFactory } from '../../__tests__/factories';

beforeEach(() => {
  jest.resetModules();
  jest.resetAllMocks();
});

describe('StepContainer', () => {
  let stepContainer: StepContainer;
  // dependencies
  const context = contextFactory.build();
  const config: StepConfig = {
    name: 'step-config',
    uses: 'https://step-url',
    needs: {
      name: 'some-name'
    }
  };

  const inputProps = {
    title: 'Props Schema',
    type: 'object',
    properties: {
      prop1: {
        type: 'string'
      },
      prop2: {
        type: 'string'
      },
      intProp: {
        type: 'integer'
      },
      boolProp: {
        type: 'boolean'
      }
    },
    additionalProperties: false,
    required: ['prop1', 'intProp', 'boolProp']
  };

  let mockLogger: MockProxy<Logger>;
  let mockNewableModule: jest.Mock<Step<unknown>, [Logger]>;
  let mockModule: MockProxy<Step<unknown>>;

  beforeEach(() => {
    mockLogger = mock<Logger>();
    mockModule = mock<Step<unknown>>();
    mockNewableModule = jest.fn<Step<unknown>, [Logger]>().mockImplementation(() => mockModule);
  });

  type RunCase = {
    name: string;
    context: WorkflowContext;
    schema: PackageSchema;
    config: StepConfig;
    expectedPackageRunProps: unknown;
  };

  const noTemplateCase: RunCase = {
    name: 'when config is valid and does not contain string templates',
    context: contextFactory.build(),
    schema: {
      events: {},
      props: inputProps
    },
    config: {
      name: 'step-config',
      uses: 'https://step-url',
      props: {
        prop1: 'prop1-value',
        prop2: 'prop2-value',
        intProp: 1,
        boolProp: true
      }
    },
    expectedPackageRunProps: {
      prop1: 'prop1-value',
      prop2: 'prop2-value',
      intProp: 1,
      boolProp: true
    }
  };

  const templateCase: RunCase = {
    name: 'when config is valid and contains templated properties',
    context: contextFactory.build({
      metadata: { name: 'workflow-context' },
      events: {
        'step-name': {
          'hello-event-name': { data: { hello: 'hello' } },
          'world-event-name': { data: { world: 'world!' } }
        },
        'step1-name': {
          'event1-name': { data: { boolProp: false, numProp2Map: 2 } }
        }
      }
    }),
    schema: {
      events: {},
      props: inputProps
    },
    config: {
      name: 'step-config',
      uses: 'https://step-url',
      props: {
        prop1:
          '{{ events.step-name.hello-event-name.data.hello }} {{ events.step-name.world-event-name.data.world }}',
        prop2: '{{ events.step-name.hello-event-name.data.hello }}',
        intProp: 1,
        boolProp: '{{ events.step1-name.event1-name.data.boolProp }}'
      }
    },
    expectedPackageRunProps: {
      prop1: 'hello world!',
      prop2: 'hello',
      intProp: 1,
      boolProp: false
    }
  };

  describe.each<RunCase>([noTemplateCase, templateCase])(
    'run(): $name',
    ({ context, schema, config, expectedPackageRunProps }) => {
      it('should run the step', async () => {
        const stepPackage: StepPackage = {
          module: mockNewableModule,
          schema: schema
        };
        const stepContainer = new StepContainer(context, stepPackage, config, mockLogger);
        expect(stepContainer.instanceId).not.toBeUndefined();

        await stepContainer.run();
        expect(mockModule.run).toHaveBeenCalledWith(context, expectedPackageRunProps);
      });
    }
  );

  type EventCase = {
    name: string;
    events: StepEvent[];
    expectedEvents: StepEvent[];
    // TODO: later add the schema here and check against it
  };
  describe.each<EventCase>([
    {
      name: 'when multiple separate events are fired',
      events: [
        { stepName: 'test', event: '1', data: { key: 'value-1' } },
        { stepName: 'test', event: '2', data: { key: 'value-2' } }
      ],
      expectedEvents: [
        { stepName: 'test', event: '1', data: { key: 'value-1' } },
        { stepName: 'test', event: '2', data: { key: 'value-2' } }
      ]
    }
  ])('$name', ({ events, expectedEvents }) => {
    const onTriggerEventMock = jest.fn();
    beforeEach(() => {
      const stepPackage: StepPackage = {
        module: mockNewableModule,
        schema: schema
      };
      stepContainer = new StepContainer(context, stepPackage, config, mockLogger);
      stepContainer.onEventTriggered = onTriggerEventMock;

      for (const event of events) {
        mockModule.onEventTriggered?.(event);
      }
    });
    it('should fire the event correctly', () => {
      for (let i = 0; i < expectedEvents.length; i++) {
        // eslint-disable-next-line security/detect-object-injection
        const expected = expectedEvents[i];
        expect(onTriggerEventMock).toHaveBeenNthCalledWith(i + 1, expected);
      }
    });
  });

  describe('dispose', () => {
    beforeEach(async () => {
      const stepPackage: StepPackage = {
        module: mockNewableModule,
        schema: schema
      };
      stepContainer = new StepContainer(context, stepPackage, config, mockLogger);
      await stepContainer.dispose();
    });
    it('should dispose of the step package', () => {
      expect(mockModule.dispose).toBeCalled();
    });
  });
});
